import { useEffect, useState } from "react"
import { AirportSeasonConfigurations } from "../api/AirportSeasonConfiguration"
import { CommonErrorText, ErrorText } from "../common/errorText"
import { PageId } from "../common/pageId"
import { collapsedCardName, CommonText } from "../common/text"
import { EditMode } from "../common/transaction"
import {
  timeRangeIsValid,
  dateRangeIsValid
} from "../components/PageConfigUtils"
import { IFieldValues } from "../containers/selectionSlice"

interface ISelectedAirportsObj {
  airportSeasons: AirportSeasonConfigurations
  airports: string[]
  countries: string[]
  season: string
  useLocalTime: boolean
}

function useErrorHandling(
  pageId: string,
  selectedAirportsObj: ISelectedAirportsObj,
  initialValues: IFieldValues,
  field: string,
  error: boolean,
  isMulti: boolean,
  pageGroup: string,
  useOwnOperators?: boolean,
  operatorOptions?: string[],
  historyRowAirports?: string[],
  editMode?: string
) {
  const { airportSeasons, airports, countries, season, useLocalTime } =
    selectedAirportsObj

  const selectedAirports = airports.length
    ? airports
    : countries.length
    ? airportSeasons
        .filter(
          (as) =>
            as.season === season &&
            countries.indexOf(as.country) >= 0 &&
            (useLocalTime ? as.supportsLt : as.supportsUtc)
        )
        .map((a: IFieldValues) => a.airport)
    : []
  const selectedSeason = selectedAirportsObj.season

  const filteredAirportSeasonConfig = airportSeasons.filter(
    (a) => a.season === selectedSeason
  )

  const combinedSelectedAirports = [...airports, ...countries]
  const airportsLengthError = combinedSelectedAirports.length === 0

  const noOperatorOptions = operatorOptions && operatorOptions.length === 0
  const noOperators = useOwnOperators && noOperatorOptions

  const initialValueCopy = { ...initialValues }
  const { startDate, endDate } = initialValueCopy

  const [invalidFieldsArrs, setInvalidFieldsArrs] = useState([])

  const [errorMsg, setErrorMsg] = useState("")

  const [showAlert, setShowAlert] = useState(false)

  //#region locked or disabled
  const lockedOrDisabled = (airportSeasons: any, key: string) => {
    return airportSeasons.filter(
      (airportSeason: boolean) => airportSeason[key] === true
    )
  }

  const notSupportDownload = (airportSeasons: any, key: string) => {
    return airportSeasons.filter(
      (airportSeason: boolean) => airportSeason[key] === false
    )
  }

  const [errors, setErrors] = useState({
    disabledOrlockedError: false,
    useOwnOperatorErrors: false,
    emptyAirportField: false,
    clearedTimeError: false,
    requiredTimeError: false,
    dateRangeError: false,
    singleDateError: false,
    isAvailDisabledError: false
  })
  const [errorField, setErrorField] = useState([])
  const disabledAirport = lockedOrDisabled(
    filteredAirportSeasonConfig,
    "isDisabled"
  )
  const lockedAirport = lockedOrDisabled(
    filteredAirportSeasonConfig,
    "isLocked"
  )
  const supportsDownload = notSupportDownload(
    airportSeasons,
    "supportsDownload"
  )

  const disabledAirportArr = disabledAirport.map((item: any) => item.airport)
  const lockedAirportArr = lockedAirport.map((a: any) => a.airport)
  const notSupportDownloadArr = supportsDownload.map(
    (item: any) => item.airport
  )
  const combinedLockedOrDisabled = [...disabledAirportArr, ...lockedAirportArr]

  const lockedOrDisabledArr =
    editMode === EditMode.EDIT ? combinedLockedOrDisabled : disabledAirportArr

  const everyIncludes = (airports: string[], arr: string[], arr1: string[]) => {
    const invalidArr =
      airports && airports.length > 0 && isMulti
        ? airports.every((value: string) => arr.includes(value))
        : arr1 && arr1.includes(airports[0])
    return invalidArr
  }
  const historyArr = historyRowAirports ? historyRowAirports : []
  const disabledAllEqual = everyIncludes(
    airports,
    lockedOrDisabledArr,
    disabledAirportArr
  )

  const notDownloadAllEqual = everyIncludes(
    historyArr,
    notSupportDownloadArr,
    historyArr
  )

  function findCommonElements(arr1: string[], arr2: string[]) {
    return arr1.some((item) => arr2.includes(item))
  }

  const disabledSomeEqual = findCommonElements(lockedOrDisabledArr, airports)
  const showDownloadAlert = findCommonElements(
    notSupportDownloadArr,
    historyArr
  )

  const combinedArr = airports.filter((item) =>
    lockedOrDisabledArr.includes(item)
  )
  const chosenNotSupportDownloadArr = historyArr.filter((item) =>
    notSupportDownloadArr.includes(item)
  )
  const filteredNoDownloadArr = Array.from(new Set(chosenNotSupportDownloadArr))

  const errorText =
    combinedArr.length === 1
      ? `${combinedArr[0]} is`
      : `${combinedArr.join(", ")} are`

  //#endregion
  const isDisabledText = findCommonElements(disabledAirportArr, airports)
  const isLockedText = findCommonElements(lockedAirportArr, airports)
  useEffect(() => {
    const { clearedTimeFrom, clearedTimeTo, requiredTimeFrom, requiredTimeTo } =
      initialValueCopy
    const clearedTimeError = timeRangeIsValid(clearedTimeFrom, clearedTimeTo, 4)
    const requiredTimeError = timeRangeIsValid(
      requiredTimeFrom,
      requiredTimeTo,
      4
    )
    const isSingleDate = pageId === PageId.EditFlightsSD

    setShowAlert(disabledSomeEqual && !disabledAllEqual && isMulti)

    const dateRangeError = dateRangeIsValid(startDate, endDate)
    const singleDateError = isSingleDate && initialValueCopy.startDate === null

    const availDisabled = filteredAirportSeasonConfig.filter(
      (a) => a.airport === selectedAirports[0]
    )
    const isAvailDisabled =
      availDisabled.filter((disabled) => disabled.isAvailDisabled).length > 0

    const isDisabled =
      availDisabled.filter((disabled) => disabled.isDisabled).length > 0
    const isAvailDisabledError =
      (isAvailDisabled || isDisabled) && pageGroup === EditMode.AVAILABILITY

    setErrors({
      disabledOrlockedError: disabledAllEqual || disabledSomeEqual,
      useOwnOperatorErrors: noOperators,
      emptyAirportField: airportsLengthError,
      clearedTimeError,
      requiredTimeError,
      dateRangeError,
      singleDateError,
      isAvailDisabledError
    })

    setErrorField([
      { error: noOperators, field: collapsedCardName.flightID },
      { error: dateRangeError, field: collapsedCardName.period },
      { error: singleDateError, field: collapsedCardName.period },
      { error: clearedTimeError, field: collapsedCardName.times },
      { error: requiredTimeError, field: collapsedCardName.times }
    ])
    //Construct Error messages

    if (pageGroup === EditMode.EDIT) {
      if (isDisabledText && !isLockedText) {
        setErrorMsg(`${errorText} ${ErrorText.airportIsDisabled}`)
      }
      if (isAvailDisabledError) {
        setErrorMsg(ErrorText.isDisabled)
      }
      if (isLockedText && !isDisabledText) {
        setErrorMsg(`${errorText} ${ErrorText.airportIsLocked}`)
      }
      if (isLockedText && isDisabledText) {
        setErrorMsg(`${errorText} ${ErrorText.airportLockedOrDisabled}`)
      }
    }
    if (pageGroup === EditMode.VIEW) {
      setErrorMsg(
        `${errorText} ${
          disabledAirport.length > 0
            ? ErrorText.airportIsDisabled
            : ErrorText.airportIsLocked
        }`
      )
    }

    if (pageGroup === EditMode.AVAILABILITY) {
      if (isAvailDisabledError) {
        setErrorMsg(`${selectedAirports[0]} is ${ErrorText.airportIsDisabled}`)
      }
    }

    if (noOperators && combinedSelectedAirports.length !== 0) {
      if (airports.length > 1) {
        setErrorMsg(ErrorText.noCommonOperator)
      } else {
        setErrorMsg(ErrorText.noSingleCommonOperator)
      }
    }
    if (combinedSelectedAirports.length === 0 && !disabledAllEqual) {
      setErrorMsg(CommonErrorText.noEmptyValue)
    }

    if (clearedTimeError || requiredTimeError) {
      setShowAlert(false)
    }

    if (error === true && selectedAirports.length === 0) {
      setInvalidFieldsArrs([...invalidFieldsArrs, field])
    }
    if (error === undefined) {
      if (combinedSelectedAirports.length !== 0) {
        setInvalidFieldsArrs(invalidFieldsArrs.filter((a) => a !== "airports"))
      }
    }
    if (error === false) {
      setInvalidFieldsArrs(invalidFieldsArrs.filter((a) => a !== field))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    disabledAllEqual,
    disabledSomeEqual,
    error,
    field,
    airports,
    countries,
    noOperators,
    initialValueCopy.endDate,
    initialValueCopy.startDate,
    initialValueCopy.clearedTimeFrom,
    initialValueCopy.clearedTimeTo,
    initialValueCopy.requiredTimeFrom,
    initialValueCopy.requiredTimeTo
  ])
  let supportErrorMsg = ""
  if (showDownloadAlert && !notDownloadAllEqual) {
    const downloadErrorText =
      filteredNoDownloadArr.length === 1
        ? `${filteredNoDownloadArr[0]}/${season} doesn't`
        : `${filteredNoDownloadArr.join(", ")}/${season} don't`
    const downloadErrorWasText =
      filteredNoDownloadArr.length === 1 ? "was" : "were"

    supportErrorMsg = `${downloadErrorText} ${ErrorText.airportnotSupportDownload} ${downloadErrorWasText} ${CommonText.skipped}`
  }

  const fieldNameErrors = errorField.filter((a) => a.error).map((a) => a.field)

  return {
    errors,
    showAlert,
    invalidFieldsArrs,
    errorMsg,
    lockedOrDisabledArr,
    selectedAirportArr: selectedAirports,
    showSupportDownloadNotification: showDownloadAlert && !notDownloadAllEqual,
    supportErrorMsg,
    fieldNameErrors
  }
}

export default useErrorHandling
